import { LitElement } from "lit"
import { customElement, property } from "lit/decorators.js"
import mapboxgl from "mapbox-gl"
import type { MapControl, MapControlPosition } from "../map-control.ts"
import { MAP_EVENT, mapEvent } from "@/lib/map/types.ts"

/**
 * MapPickerControl — renders a "Pick location" toggle button.
 * When active, shows a crosshair cursor on the map and a "Confirm" button.
 * On confirm, dispatches `gps-picked` CustomEvent with `detail: { lat, lng }`
 * on the host map element.
 */
@customElement("map-picker-control")
export class MapPickerControl extends LitElement implements MapControl {
  override createRenderRoot() {
    return this
  }

  @property({ attribute: "position" })
  accessor position: MapControlPosition = "top-right"

  private _map: mapboxgl.Map | null = null
  private _container: HTMLElement | null = null
  private _active = false

  private _onMapClick = (e: mapboxgl.MapMouseEvent) => {
    const { lat, lng } = e.lngLat
    this._deactivate()
    const mapEl = this.closest("trip-map, globe-view")
    mapEl?.dispatchEvent(mapEvent(MAP_EVENT.gpsPicked, { lat, lng }))
  }

  onAdd(map: mapboxgl.Map): HTMLElement {
    this._map = map
    this._container = document.createElement("div")
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group"
    this._container.style.cssText = `
      border-radius: 4px; overflow: hidden;
      box-shadow: 0 0 0 2px rgba(0,0,0,0.1);
    `
    this._render()
    return this._container
  }

  onRemove(_map: mapboxgl.Map): void {
    this._deactivate()
    this._container?.remove()
    this._container = null
    this._map = null
  }

  private static readonly PIN_SVG =
    `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M8 1.5a4.5 4.5 0 0 0-4.5 4.5c0 3.2 4.5 8.5 4.5 8.5s4.5-5.3 4.5-8.5A4.5 4.5 0 0 0 8 1.5zm0 6.25a1.75 1.75 0 1 1 0-3.5 1.75 1.75 0 0 1 0 3.5z"/></svg>`

  private _render() {
    if (!this._container) return
    this._container.innerHTML = ""

    const btn = document.createElement("button")
    btn.type = "button"
    btn.innerHTML = MapPickerControl.PIN_SVG
    btn.style.cssText = `
      width: 29px; height: 29px;
      display: flex; align-items: center; justify-content: center;
      border: none; cursor: pointer; transition: background 0.15s;
      background: ${this._active ? "var(--color-link, #3b82f6)" : "var(--color-surface, #fff)"};
      color: ${this._active ? "var(--color-surface, #fff)" : "var(--color-link, #374151)"};
    `
    btn.title = this._active
      ? "Click map to add a new POI (click again to cancel)"
      : "Pick a location to add a new POI"
    btn.setAttribute("aria-label", "Pick location")
    btn.setAttribute("aria-pressed", this._active ? "true" : "false")
    btn.addEventListener("click", () => {
      if (this._active) this._deactivate()
      else this._activate()
    })
    this._container.appendChild(btn)
  }

  private _activate() {
    if (!this._map) return
    this._active = true
    this._map.getCanvas().style.cursor = "crosshair"
    this._map.on("click", this._onMapClick)
    this._render()
  }

  private _deactivate() {
    this._active = false
    if (this._map) {
      this._map.getCanvas().style.cursor = ""
      this._map.off("click", this._onMapClick)
    }
    this._render()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "map-picker-control": MapPickerControl
  }
}
